import React, { useMemo, useState } from 'react';

import useDepartmentStore from '../../stores/departmentStore';
import { SPECIALIZATION_LABELS } from '../../constants/staff';
import {
  resolveDepartmentPicker,
  filterProvidersByDept,
  rederiveDepartmentOnProviderChange,
} from '../visit/departmentResolution';
import { DESK_DEPT_ALL_COPY } from './deskFilters';
import {
  ADMISSION_STATUS, AGE_MAX_LENGTH, AGE_FORMAT_HELPER_COPY, AGE_INVALID_COPY, isValidAge,
  ADMISSION_ACCESS_ERROR_CODE, ADMISSION_ACCESS_ERROR_COPY, isMoneyHidden, isEmergencySource, outOfDepartmentCopy,
  doctorDisplayName, CHANGE_DOCTOR_DEPT_FILTER_LABEL, CARE_TEAM_UNNAMED_DOCTOR, changePrimaryHintCopy,
} from '../../constants/admission';
import { useScope } from '../../context/PermissionContext';
import WardBedPicker from './WardBedPicker';
import AttenderFields from './AttenderFields';
import EstimatePanel from './EstimatePanel';
import PlannedInterventionsPicker from './PlannedInterventionsPicker';
import { useDoctorRoster } from './hooks';
import { getPatient, updatePatientDemographics } from '../../services/api/visitApi';
import AddressBlock from '../common/AddressBlock';
import { ADDRESS_FIELDS, PINCODE_ERROR, pickAddress } from '../../utils/patientAddress';
import { ageFromDob, serializeAgeValue } from '../../utils/ageValue';
import { getTodayIST } from '../../utils/dateIST';
import { ABHA_STATUS } from '../../constants/hipRecordEnums';

const overlayCls = 'fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4';
const panelCls = 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto';
const drawerCls = 'bg-white dark:bg-slate-800 border-l border-slate-200 dark:border-slate-700 shadow-xl w-full max-w-xl h-full overflow-y-auto ml-auto';
const btnPrimary = 'px-4 py-2 rounded-lg bg-teal-600 text-white text-sm font-semibold disabled:opacity-60';
const btnSecondary = 'px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 text-sm';

/**
 * admission-access C-1 — a desk action on an admission another department owns
 * is refused with 403 OUT_OF_DEPARTMENT_SCOPE; show the frozen PO copy
 * (`ADMISSION_ACCESS_ERROR_COPY`), never the wire message. Any other failure
 * keeps today's server message.
 */
const DIALOG_CODED_COPY = new Set([
  ADMISSION_ACCESS_ERROR_CODE.OUT_OF_DEPARTMENT_SCOPE,
  // M-BE-1a: POST /primary-doctor loses the care-team CAS to a concurrent
  // add/remove → 409 (ratified #4; the desk reloads before this shows).
  ADMISSION_ACCESS_ERROR_CODE.CARE_TEAM_CONFLICT,
]);
const dialogErrorMessage = (res, fallback, role) => (
  DIALOG_CODED_COPY.has(res.code)
    ? (res.code === ADMISSION_ACCESS_ERROR_CODE.OUT_OF_DEPARTMENT_SCOPE
      ? outOfDepartmentCopy(role)
      : ADMISSION_ACCESS_ERROR_COPY[res.code])
    : (res.error || fallback)
);

/**
 * S7-AC13 transfer confirm (ratified, copy-ratified.md #28) — shown to a walled
 * user only. The doctor name goes through `doctorDisplayName` ("Dr Dr." is
 * structurally impossible).
 */
const transferConfirmCopy = (doctorName, departmentName) =>
  `${doctorDisplayName(doctorName)} is in ${departmentName}. After this change the patient moves to ${departmentName} and leaves your list.`;

function ErrorNote({ testId, children }) {
  return (
    <div className="rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 text-[12.5px] p-2.5" data-testid={testId}>
      {children}
    </div>
  );
}

function Header({ title, onClose }) {
  return (
    <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center">
      <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">{title}</h3>
      <button type="button" onClick={onClose} className="ml-auto text-slate-500 dark:text-slate-400" aria-label="Close">✕</button>
    </div>
  );
}

/**
 * AssignBedDialog (S4) — a right drawer reusing WardBedPicker to attach a bed to
 * an ADMITTED no-bed row. On a 409 BED_OCCUPIED the row stays bed-less and the
 * user re-picks (no retype); the parent handles the toast + list refresh.
 */
export function AssignBedDialog({ admission, onClose, onSubmit }) {
  const { role } = useScope();
  const [bed, setBed] = useState(null);
  const [busy, setBusy] = useState(false);
  const [conflict, setConflict] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    if (!bed) return;
    setBusy(true);
    setConflict(false);
    setError(null);
    const res = await onSubmit(bed.bed_id);
    setBusy(false);
    if (res && res.code === 'BED_OCCUPIED') {
      setBed(null); // keep the drawer open, drop the pick, re-pick
      setConflict(true);
    } else if (res && !res.success) {
      setError(dialogErrorMessage(res, 'Failed to assign bed.', role));
    }
  };

  return (
    <div className={overlayCls} data-testid="assign-bed-dialog">
      <div className={drawerCls}>
        <Header title={`Assign bed — ${admission.patient_name || admission.patient_id}`} onClose={onClose} />
        <div className="p-4">
          {conflict && (
            <div className="mb-3 rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 text-[12.5px] p-2.5" data-testid="assign-bed-conflict">
              <b>That bed was just taken.</b> The patient stays without a bed until you pick another.
            </div>
          )}
          {error && <div className="mb-3"><ErrorNote testId="assign-bed-error">{error}</ErrorNote></div>}
          <WardBedPicker
            anchorDeptId={admission.department_id || null}
            value={bed ? bed.bed_id : null}
            onChange={setBed}
          />
        </div>
        <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-2">
          <button type="button" className={btnSecondary} onClick={onClose}>Cancel</button>
          <button type="button" className={btnPrimary} onClick={submit} disabled={!bed || busy} data-testid="assign-bed-confirm">
            {busy ? 'Assigning…' : 'Assign bed'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * ChangeDoctorDialog — change-primary-doctor intent. Server re-derives dept +
 * refreshes denorm; the FE previews the derived dept (A2 pattern, ONE source).
 */
export function ChangeDoctorDialog({ admission, onClose, onSubmit, deptScoped = false }) {
  const departments = useDepartmentStore((s) => s.departments);
  const { viewerDeptIds, role } = useScope();
  const deptsByMember = useDepartmentStore((s) => s.deptsByMember);
  // Same roster source as the admit form — names resolved via mapDoctorsResponse,
  // so options render "Dr. {name}", never a raw member UUID.
  const doctors = useDoctorRoster();
  const [doctorId, setDoctorId] = useState(admission.primary_doctor_member_id || '');
  const [deptId, setDeptId] = useState(admission.department_id || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const resolution = useMemo(
    () => resolveDepartmentPicker({
      departments,
      activeProviderDepts: doctorId ? (deptsByMember[doctorId] || null) : null,
      hasActiveProvider: !!doctorId,
      visitType: 'REGULAR',
    }),
    [departments, deptsByMember, doctorId],
  );
  // Owner 2026-10-05 (bk-1678): a transfer to ANY department's doctor must be
  // reachable, so the doctor list is NOT narrowed by the admission's current
  // department (the old lock made an Ortho admission list Ortho doctors only).
  // `filterDeptId` is a convenience filter on the list ("All departments" by
  // default); the admission's department (`deptId`) follows the picked doctor.
  const [filterDeptId, setFilterDeptId] = useState('');
  const filterDepartments = useMemo(
    () => resolveDepartmentPicker({ departments, hasActiveProvider: false, visitType: 'REGULAR' }).options,
    [departments],
  );
  const doctorOptions = useMemo(
    () => filterProvidersByDept(doctors, filterDeptId, deptsByMember),
    [doctors, filterDeptId, deptsByMember],
  );

  // bk-1695 — the server derives the admission's department from the new doctor
  // (single department -> that one; no department -> none) and REJECTS an explicit
  // `department_id` unless it is one of that doctor's departments (DEPT_NOT_OF_PRIMARY).
  // So a department is sent ONLY for a doctor with 2+ departments, and only the one
  // the user chose. `null` edges = not loaded yet: send nothing, the server derives.
  const doctorDeptIds = useMemo(() => {
    const edges = doctorId ? deptsByMember[doctorId] : undefined;
    if (!Array.isArray(edges)) return null;
    const active = new Set(filterDepartments.map((o) => o.dept_id));
    return edges.map((e) => e && e.dept_id).filter((id) => active.has(id));
  }, [doctorId, deptsByMember, filterDepartments]);
  const isMultiDeptDoctor = !!doctorDeptIds && doctorDeptIds.length > 1;
  // A no-department doctor leaves the admission with no department: nothing to pick.
  const isNoDeptDoctor = !!doctorDeptIds && doctorDeptIds.length === 0;
  const shownDeptId = isNoDeptDoctor ? '' : deptId;

  // S7-AC13 — `deptScoped` is the server's word that the wall applies to this
  // viewer. The doctor list stays org-wide (a transfer is allowed); the line
  // warns before save when the department the patient will move to (auto-derived
  // from the picked doctor) is not one of the viewer's own. Emergency admissions
  // stay on every desk user's list, so the "leaves your list" claim would be false.
  const targetDept = shownDeptId ? resolution.options.find((o) => o.dept_id === shownDeptId) : null;
  const pickedDoctor = doctorId ? doctors.find((d) => d.member_id === doctorId) : null;
  const pickedName = pickedDoctor ? (pickedDoctor.name || pickedDoctor.display_name) : null;
  // #39 — a DIFFERENT pick replaces the primary, who leaves the care team.
  const showChangeHint = !!doctorId && doctorId !== admission.primary_doctor_member_id;
  const changeHint = showChangeHint
    ? changePrimaryHintCopy(
      doctorDisplayName(pickedName) || CARE_TEAM_UNNAMED_DOCTOR,
      admission.primary_doctor_member_id
        ? (doctorDisplayName(admission.primary_doctor_name) || CARE_TEAM_UNNAMED_DOCTOR)
        : null,
    )
    : null;
  const showTransferLine = deptScoped
    && !isEmergencySource(admission.source)
    && Array.isArray(viewerDeptIds) && viewerDeptIds.length > 0
    && !!targetDept && !!pickedName
    && !viewerDeptIds.includes(targetDept.dept_id);

  const onDoctor = (id) => {
    setDoctorId(id);
    const r = resolveDepartmentPicker({
      departments, activeProviderDepts: id ? (deptsByMember[id] || null) : null, hasActiveProvider: !!id, visitType: 'REGULAR',
    });
    // Follow the new doctor: their single department, else keep the current one
    // only if it is still a valid choice, else clear + force a pick (same helper
    // as the Admit form).
    setDeptId(rederiveDepartmentOnProviderChange({
      resolution: r, currentDeptId: deptId, providerCleared: !id, wasManualPick: false,
    }));
  };

  const submit = async () => {
    if (!doctorId) return;
    setBusy(true);
    setError(null);
    const res = await onSubmit({
      primary_doctor_member_id: doctorId,
      ...(isMultiDeptDoctor && deptId ? { department_id: deptId } : {}),
    });
    setBusy(false);
    if (res && !res.success) setError(dialogErrorMessage(res, 'Failed to change doctor.', role));
  };

  const inputCls = 'w-full px-2.5 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-sm';
  return (
    <div className={overlayCls} data-testid="change-doctor-dialog">
      <div className={panelCls}>
        <Header title="Change primary doctor" onClose={onClose} />
        <div className="p-4 space-y-3">
          {error && <ErrorNote testId="change-doctor-error">{error}</ErrorNote>}
          {filterDepartments.length > 0 && (
            <div>
              <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">{CHANGE_DOCTOR_DEPT_FILTER_LABEL}</label>
              <select className={inputCls} value={filterDeptId} onChange={(e) => setFilterDeptId(e.target.value)} aria-label="Doctor department filter" data-testid="change-doctor-dept-filter">
                <option value="">{DESK_DEPT_ALL_COPY}</option>
                {filterDepartments.map((o) => <option key={o.dept_id} value={o.dept_id}>{o.name}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Primary doctor</label>
            <select className={inputCls} value={doctorId} onChange={(e) => onDoctor(e.target.value)} aria-label="Primary doctor">
              <option value="">— Select doctor —</option>
              {doctorOptions.map((d) => (
                <option key={d.member_id} value={d.member_id}>
                  Dr. {d.name || d.display_name}{d.specialization ? ` — ${SPECIALIZATION_LABELS[d.specialization] || d.specialization}` : ''}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Department</label>
            <select className={inputCls} value={shownDeptId} onChange={(e) => setDeptId(e.target.value)} disabled={resolution.locked || isNoDeptDoctor} aria-label="Department">
              <option value="">— No department —</option>
              {resolution.options.map((o) => <option key={o.dept_id} value={o.dept_id}>{o.name}</option>)}
            </select>
          </div>
          {changeHint && (
            <div className="text-[12.5px] text-slate-600 dark:text-slate-300" data-testid="change-doctor-hint">
              {changeHint}
            </div>
          )}
          {showTransferLine && (
            <div
              className="rounded-lg border border-amber-400 dark:border-amber-600 bg-amber-50 dark:bg-amber-900/30 text-amber-900 dark:text-amber-100 text-[12.5px] p-2.5"
              data-testid="change-doctor-transfer-line"
            >
              {transferConfirmCopy(pickedName, targetDept.name)}
            </div>
          )}
        </div>
        <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-2">
          <button type="button" className={btnSecondary} onClick={onClose}>Cancel</button>
          <button type="button" className={btnPrimary} onClick={submit} disabled={!doctorId || busy} data-testid="change-doctor-confirm">Save</button>
        </div>
      </div>
    </div>
  );
}

/** AttenderDialog — capture/edit the attender block (incl. on emergency rows). */
export function AttenderDialog({ admission, onClose, onSubmit }) {
  const { role } = useScope();
  const [value, setValue] = useState(admission.attender || { name: '', relation: '', mobile: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const submit = async () => {
    setBusy(true);
    setError(null);
    const res = await onSubmit({
      attender: {
        name: value.name || null,
        relation: value.relation || null,
        mobile: value.mobile || null,
      },
    });
    setBusy(false);
    if (res && !res.success) setError(dialogErrorMessage(res, 'Failed to update attender.', role));
  };
  return (
    <div className={overlayCls} data-testid="attender-dialog">
      <div className={panelCls}>
        <Header title="Attender / next-of-kin" onClose={onClose} />
        <div className="p-4">
          {error && <div className="mb-3"><ErrorNote testId="attender-error">{error}</ErrorNote></div>}
          <AttenderFields value={value} onChange={setValue} idPrefix="desk-attender" />
        </div>
        <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-2">
          <button type="button" className={btnSecondary} onClick={onClose}>Cancel</button>
          <button type="button" className={btnPrimary} onClick={submit} disabled={busy} data-testid="attender-confirm">Save</button>
        </div>
      </div>
    </div>
  );
}

const addressOf = (source) => Object.fromEntries(ADDRESS_FIELDS.map((f) => [f, source[f]]));

// 'YYYY-MM-DD' -> 'DD/MM/YYYY'; anything else (empty, year-only) as stored, '—' when empty.
const formatDob = (dob) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(dob || ''));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : (dob || '—');
};

/**
 * PatientDetailsDialog — view name/mobile/address for the admission's
 * patient; mobile is the patient's identity key, never editable; name is
 * denormalized onto BOTH a visit and an admission with no admission-side
 * sync path today, so editing it from here is deliberately out of scope
 * until that's built. Address, allergies AND age (AGE-FE,
 * `AGE-EDIT-spec.md` §3.1/§4) are editable.
 *
 * Address = the shared six-field AddressBlock, open, editable for everyone
 * incl. ABHA-verified patients (OR-126, marks show ABHA / Edited). Age (OR-127/135):
 * a known DOB makes it derived + read-only (the derived value is what gets
 * sent); DOB is plain text for an ABHA-verified patient, an editable box
 * otherwise; the typed age box is live only while no DOB is known.
 *
 * Age is PATIENT-RECORD scoped, not admission-state scoped — this dialog
 * stays open/editable on a DISCHARGED/terminal admission (same as address
 * today, §4 "Read-only states"): a wrong age must be fixable after
 * discharge to reprint. No new lock is added here.
 *
 * Self-contained (fetches + submits directly), unlike the sibling dialogs
 * above which delegate to the desk's generic `onSubmit` — this hits a
 * different API surface (`GET`/`PATCH /patients/{id}`, not an admission
 * endpoint), so there is no shared `patchAdmission`-shaped action to plug
 * into. `onSaved` just tells the desk to close + refresh, same effect as
 * the other dialogs' `afterAction`.
 */
export function PatientDetailsDialog({ admission, onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [patient, setPatient] = useState(null);
  // Address as loaded (six values + server `address_source` + legacy one-liner,
  // already seeded into Address line by `pickAddress`) and as currently edited.
  // Empty until the patient loads (and if the load fails).
  const [loadedAddress, setLoadedAddress] = useState(() => pickAddress(null));
  const [address, setAddress] = useState(() => addressOf(pickAddress(null)));
  // rx-allergies IPD-FE — the SAME patient-record field the OPD pad seeds from,
  // edited here and written through the same PATCH.
  const [allergies, setAllergies] = useState('');
  // OR-127/135 — a known DOB makes age derived + read-only; the typed `age`
  // string is only used (and sent) while no DOB is known.
  const [dob, setDob] = useState('');
  const [age, setAge] = useState('');
  const [ageError, setAgeError] = useState(null);
  const [pincodeError, setPincodeError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  React.useEffect(() => {
    let cancelled = false;
    getPatient(admission.patient_id).then((p) => {
      if (cancelled) return;
      const picked = pickAddress(p);
      setPatient(p);
      setLoadedAddress(picked);
      setAddress(addressOf(picked));
      setAllergies(p?.allergies || '');
      setDob(p?.dob || '');
      setAge(p?.age || '');
      setLoading(false);
    }).catch((e) => {
      if (cancelled) return;
      setError(e.message || 'Failed to load patient details.');
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [admission.patient_id]);

  // ABHA-verified: name/DOB come from ABHA (plain text here). Everyone else:
  // DOB is an editable box.
  const verified = patient?.abha_status === ABHA_STATUS.VERIFIED;
  const derivedAge = ageFromDob(verified ? patient?.dob : dob);
  const addressEdited = ADDRESS_FIELDS.some((f) => String(address[f] ?? '').trim() !== loadedAddress[f]);

  const submit = async () => {
    // Client-side mirror of the server's regex/length rule (§3.1) —
    // rejected INLINE, never round-tripped to the API. A blank/whitespace
    // age is always valid (clears the field, `isValidAge` allows it). Only a
    // TYPED age is validated: a derived one is already well-formed.
    const trimmedAge = age.trim();
    if (!derivedAge && !isValidAge(trimmedAge)) {
      setAgeError(AGE_INVALID_COPY);
      return;
    }
    setAgeError(null);
    // The block does not report pincode validity: only a 6-digit pincode is saved.
    const pin = String(address?.address_pincode ?? '').trim();
    if (pin && !/^[0-9]{6}$/.test(pin)) {
      setPincodeError(true);
      return;
    }
    setPincodeError(false);
    setBusy(true);
    setError(null);
    try {
      await updatePatientDemographics(admission.patient_id, {
        age: derivedAge ? serializeAgeValue(derivedAge.amount, derivedAge.unit) : (trimmedAge === '' ? null : trimmedAge),
        allergies,
        admissionId: admission.admission_id,
        // Written only when changed (never echo what we merely loaded): all six
        // address keys together (server: absent = untouched, null = clear, and it
        // re-syncs the admission copy), and a staff-edited DOB for non-verified.
        ...(addressEdited ? address : {}),
        ...(!verified && dob !== (patient?.dob || '') ? { dob } : {}),
      });
      setBusy(false);
      await onSaved();
    } catch (e) {
      setError(e.message || 'Failed to save patient details.');
      setBusy(false);
    }
  };

  const inputCls = 'w-full px-2.5 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500';
  const labelCls = 'block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1';

  return (
    <div className={overlayCls} data-testid="patient-details-dialog">
      <div className={panelCls}>
        <Header title="Patient Details" onClose={onClose} />
        <div className="p-4 space-y-3">
          {error && (
            <div className="rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 text-[12.5px] p-2.5" data-testid="patient-details-error">
              {error}
            </div>
          )}
          {loading ? (
            <div className="text-[13px] text-slate-500 dark:text-slate-400">Loading…</div>
          ) : (
            <>
              <div>
                <div className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Name</div>
                <div className="text-sm text-slate-800 dark:text-slate-100">
                  {admission.patient_name || `${patient?.first_name || ''} ${patient?.last_name || ''}`.trim()}
                </div>
              </div>
              <div>
                <div className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Mobile</div>
                <div className="text-sm text-slate-800 dark:text-slate-100">{patient?.mobile || '—'}</div>
              </div>
              <div>
                {verified ? (
                  <>
                    <div className={labelCls}>Date of birth</div>
                    <div className="text-sm text-slate-800 dark:text-slate-100" data-testid="pd-dob-text">{formatDob(patient?.dob)}</div>
                  </>
                ) : (
                  <>
                    <label className={labelCls} htmlFor="pd-dob">Date of birth</label>
                    <input
                      id="pd-dob"
                      type="date"
                      className={inputCls}
                      max={getTodayIST()}
                      value={dob}
                      onChange={(e) => setDob(e.target.value)}
                      aria-label="Date of birth"
                      data-testid="pd-dob-input"
                    />
                  </>
                )}
              </div>
              <div>
                <label className={labelCls} htmlFor="pd-age">Age</label>
                {derivedAge ? (
                  <>
                    <input
                      id="pd-age"
                      type="text"
                      className={`${inputCls} bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300`}
                      value={serializeAgeValue(derivedAge.amount, derivedAge.unit)}
                      readOnly
                      aria-label="Age"
                      data-testid="pd-age-input"
                    />
                    <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-1" data-testid="pd-age-derived-hint">Calculated from date of birth</div>
                  </>
                ) : (
                  <>
                    <input
                      id="pd-age"
                      type="text"
                      className={inputCls}
                      value={age}
                      onChange={(e) => { setAge(e.target.value); setAgeError(null); }}
                      aria-label="Age"
                      placeholder="45"
                      maxLength={AGE_MAX_LENGTH}
                      data-testid="pd-age-input"
                    />
                    <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">{AGE_FORMAT_HELPER_COPY}</div>
                    {ageError && (
                      <div className="text-[11.5px] text-red-600 dark:text-red-400 mt-1" data-testid="pd-age-error">{ageError}</div>
                    )}
                  </>
                )}
              </div>
              <div>
                {/* OR-126: editable for everyone, verified included (source marks show ABHA / Edited). */}
                <AddressBlock
                  value={address}
                  onChange={setAddress}
                  source={loadedAddress.address_source}
                  legacyAddress={loadedAddress.legacy}
                  mode="open"
                  idPrefix="pd-address"
                  testIdPrefix="pd-address"
                />
                {pincodeError && (
                  <div className="text-[11.5px] text-red-600 dark:text-red-400 mt-1" data-testid="pd-address-pincode-invalid">{PINCODE_ERROR}</div>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1" htmlFor="pd-allergies">Allergies</label>
                <textarea
                  id="pd-allergies"
                  className="w-full px-2.5 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                  rows={2}
                  value={allergies}
                  onChange={(e) => setAllergies(e.target.value)}
                  aria-label="Allergies"
                  placeholder="e.g. Penicillin, Sulpha drugs"
                  maxLength={500}
                />
              </div>
            </>
          )}
        </div>
        <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-2">
          <button type="button" className={btnSecondary} onClick={onClose}>Cancel</button>
          {/* `!patient` — when `getPatient` FAILED the fields render empty (the
              error banner is above), so a Save here would PATCH `address: null`
              AND `allergies: null` and wipe both. Pre-existing for address;
              phase 2 puts a clinical safety field on the same path, which is
              exactly the "a blank box never wipes the record" rule this slice
              is built on. Guarded here rather than by hiding the form, so the
              error stays visible and nothing else about the dialog moves. */}
          <button type="button" className={btnPrimary} onClick={submit} disabled={loading || busy || !patient} data-testid="patient-details-confirm">Save</button>
        </div>
      </div>
    </div>
  );
}

/**
 * CancelAdmissionDialog — cancel/void with a REQUIRED reason (§2.6). The desk
 * only opens this for a CANCELABLE status (shared CANCELABLE_STATUSES const).
 */
export function CancelAdmissionDialog({ admission, onClose, onSubmit }) {
  const { role } = useScope();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const submit = async () => {
    if (!reason.trim()) { setError('A reason is required to cancel.'); return; }
    setBusy(true);
    setError(null);
    const res = await onSubmit({ to_status: ADMISSION_STATUS.CANCELLED, reason: reason.trim() });
    setBusy(false);
    if (res && !res.success) setError(dialogErrorMessage(res, 'Failed to cancel.', role));
  };
  return (
    <div className={overlayCls} data-testid="cancel-dialog">
      <div className={panelCls}>
        <Header title="Cancel admission" onClose={onClose} />
        <div className="p-4">
          {error && <div className="mb-3"><ErrorNote testId="cancel-error">{error}</ErrorNote></div>}
          <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Reason *</label>
          <textarea
            className="w-full px-2.5 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-sm"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Admitted in error — wrong patient"
            aria-label="Cancel reason"
          />
          <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-2">Cancelling also frees the patient&rsquo;s bed.</p>
        </div>
        <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-2">
          <button type="button" className={btnSecondary} onClick={onClose}>Keep admission</button>
          <button type="button" className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold disabled:opacity-60" onClick={submit} disabled={busy} data-testid="cancel-confirm">
            Cancel admission
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * EditPlanEstimateDialog (S6/S9) — edit planned interventions + estimate on an
 * existing admission. KEPT lines send `{line_id}` (retain the frozen server
 * snapshot); NEW lines send `{type, catalog_ref}` (server snapshots now).
 */
export function EditPlanEstimateDialog({ admission, onClose, onSubmit }) {
  const { role } = useScope();
  const isElective = admission.source === 'planned';
  // admission-access — a money-hidden viewer edits expected days + the plan
  // lines only: no amounts on screen, and the request carries no money lines.
  const moneyHidden = isMoneyHidden(admission);
  const [lines, setLines] = useState(() => admission.planned_interventions || []);
  const [days, setDays] = useState(admission.estimate ? admission.estimate.expected_stay_days : null);
  const [otherLines, setOtherLines] = useState(() => (admission.estimate && admission.estimate.other_lines) || []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const bedCategory = admission.estimate
    ? {
      name: admission.estimate.bed_category_name,
      per_day_charge: admission.estimate.per_day_charge,
      nursing_charge_per_day: admission.estimate.nursing_charge_per_day,
      rmo_charge_per_day: admission.estimate.rmo_charge_per_day,
    }
    : null;

  const submit = async () => {
    setBusy(true);
    setError(null);
    const payload = {
      expected_stay_days: days ?? null,
      // Kept lines → {line_id}; new lines (no line_id) → {type, catalog_ref}.
      planned_interventions: lines.map((l) =>
        l.line_id ? { line_id: l.line_id } : { type: l.type, catalog_ref: l.catalog_ref }),
    };
    // Hidden viewer: the redacted `other_lines` ([]) is not the real estimate's,
    // so it is never sent — an absent key, not a fabricated empty money list.
    if (!moneyHidden) {
      payload.other_lines = otherLines.filter((l) => l.label).map((l) => ({ label: l.label, amount: Number(l.amount) || 0 }));
    }
    const res = await onSubmit(payload);
    setBusy(false);
    if (res && !res.success) setError(dialogErrorMessage(res, 'Failed to update estimate.', role));
  };

  return (
    <div className={overlayCls} data-testid="edit-plan-dialog">
      <div className={panelCls}>
        <Header title="Edit plan / estimate" onClose={onClose} />
        <div className="p-4 space-y-4">
          {error && <ErrorNote testId="edit-plan-error">{error}</ErrorNote>}
          {isElective && !moneyHidden && (
            <PlannedInterventionsPicker
              anchorDeptId={admission.department_id || null}
              onAdd={(line) => setLines((l) => [...l, line])}
            />
          )}
          <EstimatePanel
            value={{ expectedStayDays: days, bedCategory, plannedInterventions: lines, otherLines }}
            onChangeDays={setDays}
            onChangeOtherLines={setOtherLines}
            onRemoveIntervention={(i) => setLines((l) => l.filter((_, idx) => idx !== i))}
            admissionId={admission.admission_id}
            moneyHidden={moneyHidden}
          />
        </div>
        <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-2">
          <button type="button" className={btnSecondary} onClick={onClose}>Cancel</button>
          <button type="button" className={btnPrimary} onClick={submit} disabled={busy} data-testid="edit-plan-confirm">Save estimate</button>
        </div>
      </div>
    </div>
  );
}
